import React, { useEffect, useMemo, useRef, useState } from "react";
import { Select } from "antd";

import styles from "./pricesSelectionForm.scss";
import { api, localStorage } from "../../../services";
import { HrCountryId, LocalStorageKey } from "../../../models";
import {
  buildPropertyTypeOptions,
  CategoryLike,
  LocationOption,
  locationOptionFromCounty,
  locationOptionFromResolved,
  locationOptionFromSearchResult
} from "../helpers";
import {
  useLocationChildren,
  useResolvedLocation
} from "../../../routes/Configuration/hooks";
import { LocationSearchResultI } from "../../../typings/configurationTypes";

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 200;
const MAX_SEARCH_RESULTS = 50;

export interface PricesSelectionFormProps {
  categories: CategoryLike[];
  initialPropertyType?: string;
  initialLocation?: LocationOption | null;
  onSubmit: (propertyType: string, location: LocationOption) => void;
  className?: string;
}

const PricesSelectionForm = (props: PricesSelectionFormProps) => {
  const {
    categories,
    initialPropertyType,
    initialLocation,
    onSubmit,
    className
  } = props;

  const [selectedLocation, setSelectedLocation] = useState(initialLocation || null);
  const [searchValue, setSearchValue] = useState("");
  const noResults: LocationOption[] = [];
  const [searchResults, setSearchResults] = useState(noResults);
  const [propertyType, setPropertyType] = useState(initialPropertyType);
  const [restoredFromStorage, setRestoredFromStorage] = useState(false);

  // Query of the latest request; answers for older queries are dropped.
  const latestQueryRef = useRef("");
  const searchTimeoutRef = useRef(null);

  const propertyTypeOptions = useMemo(() => {
    return buildPropertyTypeOptions(categories);
  }, [categories]);

  // Default options (nothing typed yet): Croatian counties
  const counties = useLocationChildren(HrCountryId);
  const countyOptions = useMemo(
    () => (counties || []).map(locationOptionFromCounty),
    [counties]
  );

  // Restore from localStorage if no initial values provided: the stored ids
  // are resolved through configuration/locations/resolve.
  const storedSelection = useMemo(
    () => localStorage.getItem(LocalStorageKey.realEstatePricesSelection),
    []
  );
  const storedLocation = storedSelection && storedSelection.propertyLocation;
  const storedLocationId =
    !initialLocation && storedLocation
      ? storedLocation.settlementId || storedLocation.cityId || storedLocation.countyId
      : undefined;
  const resolvedStoredLocation = useResolvedLocation(storedLocationId);

  useEffect(() => {
    if (restoredFromStorage) return;

    if (!storedSelection) {
      setRestoredFromStorage(true);
      return;
    }

    // Wait for the stored location to resolve before deciding
    if (storedLocationId && resolvedStoredLocation === undefined) return;

    if (!selectedLocation && !initialLocation && storedLocationId) {
      const location = locationOptionFromResolved(resolvedStoredLocation);

      if (location) {
        setSelectedLocation(location);
      }
    }

    // Restore property type if not already set
    if (!propertyType && !initialPropertyType && storedSelection.propertyType) {
      setPropertyType(storedSelection.propertyType);
    }

    setRestoredFromStorage(true);
  }, [resolvedStoredLocation, restoredFromStorage, selectedLocation, initialLocation, propertyType, initialPropertyType]);

  // Sync state when initial props change (e.g., passed from parent)
  useEffect(() => {
    if (initialLocation && !selectedLocation) {
      setSelectedLocation(initialLocation);
    }
  }, [initialLocation]);

  useEffect(() => {
    if (initialPropertyType && !propertyType) {
      setPropertyType(initialPropertyType);
    }
  }, [initialPropertyType]);

  // Server side search (configuration/locations/search), debounced
  useEffect(() => {
    const query = (searchValue || "").trim();
    latestQueryRef.current = query;
    clearTimeout(searchTimeoutRef.current);

    if (query.length < MIN_SEARCH_LENGTH) {
      setSearchResults([]);
      return undefined;
    }

    searchTimeoutRef.current = setTimeout(() => {
      api.Configuration.searchLocations(query)
        .then((results: LocationSearchResultI[]) => {
          if (latestQueryRef.current !== query) return;
          setSearchResults(
            (results || []).slice(0, MAX_SEARCH_RESULTS).map(locationOptionFromSearchResult)
          );
        })
        .catch(() => {
          if (latestQueryRef.current !== query) return;
          setSearchResults([]);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchValue]);

  const filteredLocations = useMemo(() => {
    let results: LocationOption[] = [];

    if (!searchValue || searchValue.length < MIN_SEARCH_LENGTH) {
      results = countyOptions.slice(0, 20);
    } else {
      results = searchResults;
    }

    if (selectedLocation && !results.find(loc => loc.key === selectedLocation.key)) {
      results = [selectedLocation, ...results];
    }

    return results;
  }, [countyOptions, searchResults, searchValue, selectedLocation]);

  const handleLocationChange = (value: string | undefined) => {
    if (!value) {
      setSelectedLocation(null);
      return;
    }
    const loc = filteredLocations.find(l => l.key === value);
    setSelectedLocation(loc || null);
  };

  const isSubmitDisabled = !propertyType || !selectedLocation;

  const handleSubmit = () => {
    if (isSubmitDisabled || !selectedLocation || !propertyType) return;
    onSubmit(propertyType, selectedLocation);
  };

  return (
    <div className={className ? className + " " + styles.card : styles.card}>
      <div className={styles.field}>
        <label className={styles.label}>{"Lokacija"}</label>
        <Select
          className={styles.select}
          placeholder={"Pretražite lokaciju..."}
          value={selectedLocation ? selectedLocation.key : undefined}
          onChange={handleLocationChange}
          showSearch
          filterOption={false}
          onSearch={setSearchValue}
          notFoundContent={searchValue.length < MIN_SEARCH_LENGTH ? "Unesite najmanje 2 znaka" : "Nema rezultata"}
          allowClear
        >
          {filteredLocations.map(loc => (
            <Select.Option key={loc.key} value={loc.key}>{loc.label}</Select.Option>
          ))}
        </Select>
      </div>

      <div className={styles.field}>
        <label className={styles.label}>{"Tip nekretnine"}</label>
        <Select
          className={styles.select}
          placeholder={"Odaberite tip nekretnine"}
          value={propertyType}
          onChange={setPropertyType}
        >
          {propertyTypeOptions.map(o => (
            <Select.Option key={o.slug} value={o.slug}>{o.label}</Select.Option>
          ))}
        </Select>
      </div>

      <button
        type="button"
        className={styles.submit}
        disabled={isSubmitDisabled}
        onClick={handleSubmit}
      >
        {"TRAŽI"}
      </button>
    </div>
  );
};

export default PricesSelectionForm;



// WEBPACK FOOTER //
// ./src/containers/RealEstatePricesPage/PricesSelectionForm/index.tsx